import type { dePlatform } from './de.platform';

/** English texts for the system administration. Reference: de.platform.ts */
export const enPlatform: Record<keyof typeof dePlatform, string> = {
  'platform.nav.tenants': 'Customers',
  'platform.nav.settings': 'Settings',
  'platform.nav.main': 'Main navigation',
  'platform.role': 'System administration',

  'platform.tenants.title': 'Customers',
  'platform.tenants.empty': 'No customer has registered yet.',
  'platform.tenants.customer': 'Customer',
  'platform.tenants.email': 'Email',
  'platform.tenants.registered': 'Registered',
  'platform.tenants.status': 'Status',
  'platform.tenants.callers': 'Callers',
  'platform.tenants.campaigns': 'Campaigns',
  'platform.tenants.balance': 'Credit',
  'platform.tenants.price': 'Price per minute',
  'platform.tenants.minutes': 'Minutes',
  'platform.tenants.open': 'Open',
  'platform.tenants.paymentsLater': 'Paid top-ups and granted bonus credit arrive with the payment part.',

  'platform.status.active': 'Active',
  'platform.status.suspended': 'Suspended',
  'platform.status.deleted': 'Deleted',

  'platform.tenant.back': 'Back to customers',
  'platform.tenant.account': 'Account',
  'platform.tenant.customerNumber': 'Customer number',
  'platform.tenant.accountName': 'Company / account name',
  'platform.tenant.owner': 'Account owner',
  'platform.tenant.language': 'Language',
  'platform.tenant.suspend': 'Suspend account',
  'platform.tenant.unsuspend': 'Release account',
  'platform.tenant.suspendTitle': 'Suspend account',
  'platform.tenant.suspendText':
    'All running calls end immediately, all users are signed out and cannot sign in again. You can release the account at any time.',

  'platform.tenant.usage': 'Usage',
  'platform.tenant.balance': 'Balance',
  'platform.tenant.balanceAdd': 'Add balance',
  'platform.tenant.balanceSubtract': 'Subtract balance',
  'platform.tenant.balanceAmount': 'Amount in euros',
  'platform.tenant.balanceNote': 'Reason - the customer sees it',
  'platform.tenant.balanceNotePlaceholder': 'e.g. goodwill after an outage',
  'platform.tenant.balanceBookings': 'Latest entries',
  'platform.tenant.balanceEmpty': 'There are no entries yet.',
  'platform.tenant.balanceAvailable': 'Available',
  'platform.tenant.balanceDebitTitle': 'Subtract balance',
  'platform.tenant.balanceDebitText':
    'The amount is subtracted immediately. The reason appears in the customer payment history.',
  'platform.tenant.balanceDebitConfirm': 'Subtract now',
  'platform.tenant.balanceInternalNote': 'Internal note',
  'platform.tenant.usageMinutes': 'Billed minutes',
  'platform.tenant.usageToday': 'Minutes today',
  'platform.tenant.usageConnected': 'Connected calls',
  'platform.tenant.usageAttempts': 'Dial attempts',
  'platform.tenant.usageLast': 'Last call',

  'platform.tenant.export': 'Customer data export',
  'platform.tenant.exportHint':
    'One ZIP file with one CSV per campaign. Each campaign has its own fields, hence one file per campaign.',
  'platform.tenant.exportAction': 'Download all customer data',
  'platform.tenant.exportScope':
    'This covers the regular records. Archived and customer-deleted data sit in the archive.',

  'platform.tenant.settings': 'Customer settings',
  'platform.tenant.settingsHint': 'Read-only here - the customer changes them.',
  'platform.tenant.settingsCountries': 'Country codes',
  'platform.tenant.settingsInbound': 'Campaigns with incoming calls',
  'platform.tenant.settingsQueue': 'Campaigns with a queue',
  'platform.tenant.settingsRecording': 'Campaigns with recording',

  'platform.tenant.archive': 'Archive and deletion',
  'platform.tenant.archiveSoon':
    'The archive of customer-deleted data and the final deletion are coming in one of the next steps.',

  'platform.live.title': 'Calls in progress',
  'platform.live.hint': 'Only calls where people are actually talking to each other.',
  'platform.live.empty': 'No call is running for this customer right now.',
  'platform.live.campaign': 'Campaign',
  'platform.live.caller': 'Caller',
  'platform.live.customer': 'Customer',
  'platform.live.duration': 'Duration',
  'platform.live.listen': 'Listen in',
  'platform.live.stopListening': 'Stop listening',
  'platform.live.listening': 'You are listening',
  'platform.live.silent':
    'You can only listen. Speaking is not possible, and nobody on the call is told about it.',

  'platform.recordings.title': 'Recordings',
  'platform.recordings.hint': 'Download per campaign. Recordings are deleted for good after 30 days.',
  'platform.recordings.campaign': 'Campaign',
  'platform.recordings.count': 'Available',
  'platform.recordings.range': 'Period',
  'platform.recordings.download': 'All as ZIP',
  'platform.recordings.empty': 'There are no recordings for this customer.',
  'platform.recordings.none': 'none',
  'platform.recordings.notYet':
    'Call recording itself is coming in one of the next steps. Until then this list stays empty.',

  'platform.period.today': 'Today',
  'platform.period.week': 'Week',
  'platform.period.month': 'Month',
  'platform.period.all': 'All',
  'platform.period.label': 'Period',
  'platform.period.help':
    'Counted in server time: today from 00:00, week from Monday 00:00, month from the first.',

  'platform.settings.title': 'Global settings',
  'platform.settings.price': 'Default price per minute',
  'platform.settings.startCredit': 'Starting credit',
  'platform.settings.minTopUp': 'Minimum top-up amount',
  'platform.settings.minTopUpHint':
    'Applies only when a customer tops up themselves. It does not apply to your own entries.',
  'platform.settings.retryInterval': 'Minimum gap between retries (minutes)',
  'platform.settings.supportEmail': 'Support email',
  'platform.settings.saved': 'The settings have been saved.',
  'platform.settings.smtpSoon': 'The SMTP settings arrive with the email part.',

  'error.tenant.not_found': 'This customer was not found.',
  'error.call.not_found': 'This call is no longer running.',
  'error.monitoring.unavailable': 'This call cannot be listened to right now.',
  'error.recordings.invalid_period': 'Please choose a valid period.',
  'error.auth.platform_admin_required': 'This area is not available.',
};
